import {scheduleBackwardChain} from '../craft/backward-chain.js';
import {
  PlannerBlockKind,
  PlannerDurationKind,
  createPlannerRequest,
  createPlannerSchedule,
  createPlannerScheduledBlock,
  createPlannerWarning
} from './schedule-model.js';

function iso(millis){
  return new Date(millis).toISOString();
}

function warningFor(block){
  if(block.durationKind===PlannerDurationKind.MISSING){
    return createPlannerWarning({
      code:'MISSING_DURATION',
      message:'Brakuje czasu dla kroku „'+block.label+'”. Dymistrz nie zgaduje czasu procesu.',
      blockId:block.id,
      sourceRef:block.sourceRef
    });
  }
  if(block.durationKind===PlannerDurationKind.ESTIMATE){
    return createPlannerWarning({
      code:'ESTIMATED_DURATION',
      message:'Czas kroku „'+block.label+'” jest estymacją i wymaga uwzględnienia niepewności.',
      blockId:block.id,
      sourceRef:block.sourceRef
    });
  }
  return null;
}

function scheduleBackward(blocks,endMillis,{lane,itemId=null}={}){
  const value=scheduleBackwardChain(blocks,Number.isFinite(endMillis)?iso(endMillis):null,{lane,itemId});
  return Object.freeze({
    blocks:Object.freeze(value.blocks.map(createPlannerScheduledBlock)),
    warnings:Object.freeze(blocks.map(warningFor).filter(Boolean)),
    startMillis:value.startMillis
  });
}

function chronological(left,right){
  const leftStart=left.startAt==null?Number.POSITIVE_INFINITY:Date.parse(left.startAt);
  const rightStart=right.startAt==null?Number.POSITIVE_INFINITY:Date.parse(right.startAt);
  if(leftStart!==rightStart)return leftStart-rightStart;
  const leftEnd=left.endAt==null?Number.POSITIVE_INFINITY:Date.parse(left.endAt);
  const rightEnd=right.endAt==null?Number.POSITIVE_INFINITY:Date.parse(right.endAt);
  return leftEnd-rightEnd
    ||String(left.lane).localeCompare(String(right.lane))
    ||left.sequence-right.sequence
    ||left.id.localeCompare(right.id);
}

export function generateBackwardSchedule({
  source,
  targetReadyAt,
  timeZone=null,
  bufferMinutes=0
}={}){
  const request=createPlannerRequest({
    source,
    targetReadyAt,
    timeZone,
    bufferMinutes
  });
  const targetMillis=Date.parse(request.targetReadyAt);
  const bufferMillis=request.bufferMinutes*60_000;
  const processEndMillis=targetMillis-bufferMillis;

  const blocks=[];
  const warnings=[];

  const process=scheduleBackward(
    request.source.processBlocks,
    processEndMillis,
    {lane:'process'}
  );
  blocks.push(...process.blocks);
  warnings.push(...process.warnings);

  for(const chain of request.source.itemChains){
    const item=scheduleBackward(
      chain.blocks,
      process.startMillis,
      {lane:'item:'+chain.itemId,itemId:chain.itemId}
    );
    blocks.push(...item.blocks);
    warnings.push(...item.warnings);
  }

  if(request.bufferMinutes>0){
    blocks.push(createPlannerScheduledBlock({
      id:'planner:buffer',
      kind:PlannerBlockKind.BUFFER,
      label:'Bufor bezpieczeństwa czasu',
      durationMinutes:request.bufferMinutes,
      durationKind:PlannerDurationKind.FIXED,
      startAt:iso(processEndMillis),
      endAt:iso(targetMillis),
      sourceRef:null,
      sequence:1000000,
      lane:'buffer'
    }));
  }

  const scheduledStarts=blocks
    .map(block=>block.startAt)
    .filter(Boolean)
    .map(Date.parse);
  const earliestStartAt=scheduledStarts.length?iso(Math.min(...scheduledStarts)):null;

  const hasMissing=blocks.some(block=>block.durationKind===PlannerDurationKind.MISSING);
  const hasUnscheduled=blocks.some(block=>block.startAt==null||block.endAt==null);
  const complete=!hasMissing&&!hasUnscheduled&&process.startMillis!=null;

  return createPlannerSchedule({
    request,
    blocks:[...blocks].sort(chronological),
    warnings,
    complete,
    processStartAt:process.startMillis==null?null:iso(process.startMillis),
    processEndAt:iso(processEndMillis),
    earliestStartAt
  });
}
